import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '@unified-checkout/design-system';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { IUserCheck } from '@app/dashboard/types/outerLayout/forget-password';
import { isFormValid, required } from '@app/dashboard/utils/formValidations';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { checkUser } from '@app/dashboard/services/outer_layout/forget-password';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { addToLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import styles from "../forget-password.module.scss";

const FieldNames = {
  USER_NAME_OR_PHONE_NUMBER: "username_or_phonenumber"
};

const SUBMIT_BTN_ID = 'forget-password-user-check-submit-btn';

const UserCheck = (
  {
    setEmailOrPhoneNumber,
    validateField,
    loading,
    setLoading,
    setCurrentStep,
    STEPS,
    redirectToRegister,
    country
  }: IUserCheck
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const fieldsInitialState = {
    [FieldNames.USER_NAME_OR_PHONE_NUMBER]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    },
  };

  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [serverErrMsg, setServerErrMsg] = useState('');

  const handlePhoneChange = (number: string) => {
    setServerErrMsg("");
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    const newState = fields;
    newState[FieldNames.USER_NAME_OR_PHONE_NUMBER].value = numWithoutPhoneCountryCode;

    setFields(newState);
    validateField(fields, setFields, FieldNames.USER_NAME_OR_PHONE_NUMBER, numWithoutPhoneCountryCode);
  };

  const handleSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const usernameOrPhone = fields[FieldNames.USER_NAME_OR_PHONE_NUMBER].value as string;

    checkUser(usernameOrPhone)
      .then(async (response) => {
        const { data: { old_user, token, otp_sent_to } } = response;

        await addToLocalStorage(LOCAL_STORAGE_KEYS.SIGNUP_BY_PHONE_TOKEN, token);

        old_user ?
          setCurrentStep(STEPS.PHONE_NUMBER_VERIFICATION)
          :
          (
            setEmailOrPhoneNumber(otp_sent_to),
            setCurrentStep(STEPS.OTP)
          );

      })
      .catch((error) => {
        const message = error.response?.data?.message;
        setServerErrMsg(message || t('errors.somethingWentWrong'))
        // setServerErrMsg(message ? t('outerLayout.form.forgetPassword.errors.checkUserNameOrPhone') : t('errors.somethingWentWrong'))
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Input
        id={FieldNames.USER_NAME_OR_PHONE_NUMBER}
        name={FieldNames.USER_NAME_OR_PHONE_NUMBER}
        type='text'
        label={t('outerLayout.form.labels.emailOrUsernameOrPhone')}
        placeholder={t('outerLayout.form.labels.enterEmailOrUsernameOrPhone')}
        value={fields[FieldNames.USER_NAME_OR_PHONE_NUMBER].value}
        required
        onChange={(e) => { handlePhoneChange(e.target.value) }}
        status={fields[FieldNames.USER_NAME_OR_PHONE_NUMBER].isValid === false ? 'error' : 'default'}
        error={fields[FieldNames.USER_NAME_OR_PHONE_NUMBER].errorMsg}
        preventAutoComplete
      />
      <div className='form__submit'>
        {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.forgetPassword.continue')}
          loading={loading}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={handleSubmit}
        />
      </div>
      <div className={styles["forget-password-form__dont-have-account"]}>
        <span className={styles["forget-password-form__dont-have-account__text"]}>{t('outerLayout.form.dontHaveAcc')}</span>
        <div className={styles["forget-password-form__dont-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.signUp')}
            onClickHandler={redirectToRegister}
          />
        </div>
      </div>
    </>
  )
}

export default UserCheck;